import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AddTechnicianDialog({ open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [specializations, setSpecializations] = React.useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("technicians").insert({
        name,
        phone: phone || null,
        email: email || null,
        specializations: specializations.split(",").map((s) => s.trim()).filter(Boolean),
        status: "active",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["technicians-list"] });
      toast.success("Technician added!");
      resetForm();
      onOpenChange(false);
    },
    onError: () => toast.error("Failed to add technician."),
  });

  const resetForm = () => {
    setName("");
    setPhone("");
    setEmail("");
    setSpecializations("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Technician</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div>
            <Label htmlFor="add-name">Name *</Label>
            <Input id="add-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="add-phone">Phone</Label>
            <Input id="add-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="add-email">Email</Label>
            <Input id="add-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="add-specs">Specializations (comma-separated)</Label>
            <Input id="add-specs" value={specializations} onChange={(e) => setSpecializations(e.target.value)} placeholder="e.g. splicing, testing" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>{mutation.isPending ? "Adding…" : "Add Technician"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
